import React, { useState, useEffect } from "react";
import { Box, Button, Flex, Menu, MenuItem, Table, Typography } from "flux";
import useAdAssetAdImages from "../../hooks/use-ad-asset-ad-images";
import { useParams } from "react-router-dom";
import { Image } from "@adora/shared/types/image";
import { useFileDownload } from "@adora/shared/hooks/use-file-download";
import useMutateAdAssetGroup from "../../hooks/use-mutate-ad-asset-group";
import { resizeImages, downloadResizedImages } from "../../utils/resizer";
import { useToast } from "@adora/shared/hooks/use-toast";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { createDownloadTrackingCallbacks } from "@web/utils";
import ResizedImageDialog from "../../components/resized-images-dialog";

export interface AdImagesTableProps {
  adAssetGroupId: number;
}

interface MenuAction {
  label: string;
  onClick: () => void;
  visible?: (row?: Image) => boolean;
}

const AdImagesTable: React.FC<AdImagesTableProps> = ({ adAssetGroupId }) => {
  const { brandId } = useParams<{ brandId: string }>();
  const [canResize, setCanResize] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<Image | undefined>();
  const [canDownloadResized, setCanDownloadResized] = useState<boolean>(false);
  const { images, isLoading } = useAdAssetAdImages(adAssetGroupId, brandId);
  const { downloadAdAssetGroup } = useMutateAdAssetGroup();
  const { downloadFile, isDownloading } = useFileDownload();
  const { openToast } = useToast();
  const { openDialog } = useDialog();

  useEffect(() => {
    const resizedImages = images.filter((image) => image.sizes && image.sizes.length > 0);
    setCanDownloadResized(resizedImages.length === images.length);
  }, [images]);

  async function handleDownloadAll() {
    if (!brandId) return;

    await downloadFile(() => downloadAdAssetGroup(adAssetGroupId, Number.parseInt(brandId)), {
      fileName: "ad-images",
      fileType: "zip",
      ...createDownloadTrackingCallbacks({
        feature: "internal_ad_asset_group",
        download_type: "bulk",
        ad_asset_group_id: adAssetGroupId,
        brand_id: Number.parseInt(brandId)
      }),
      onError: (error) => {
        console.error("Download failed:", error);
      }
    });
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, imageId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    const image = images.find((b) => b.id === imageId);
    setSelectedRow(image);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(undefined);
  };

  const handleViewAllResizedImages = async () => {
    if (!brandId) return;

    if (selectedRow) {
      handleMenuClose();
      await openDialog(ResizedImageDialog, {
        adAssetGroupId: adAssetGroupId,
        image: selectedRow,
        brandId: Number.parseInt(brandId)
      });
    }
  };

  const ImagePreviewCell: React.FC<{ image?: Image }> = ({ image }) => {
    if (!image) return <Typography color="textSecondary">No image</Typography>;
    return <img src={image.src} alt="Preview" width={150} height={150} style={{ objectFit: "contain" }} />;
  };

  const handleResizeAll = async () => {
    handleResize();
  };

  const handleResize = async (image?: Image) => {
    if (!brandId) return;

    setCanResize(false);
    const response = await resizeImages({
      adAssetGroup: adAssetGroupId,
      adImageIds: image ? [image.id] : images.map((image) => image.id),
      brandId: Number.parseInt(brandId)
    });
    response.match(
      (error) => {
        if (error.tag == "validationError") {
          error.errors.map((message) => openToast(message));
        } else {
          openToast("An unknown error occured");
        }
      },
      () => {
        openToast("Job submitted successfully");
      }
    );
    setCanResize(true);
  };

  const handleDownloadResized = async () => {
    if (!brandId) return;
    setCanResize(false);
    setCanDownloadResized(false);
    await downloadFile(
      () => downloadResizedImages({ adAssetGroup: adAssetGroupId, brandId: Number.parseInt(brandId) }),
      {
        fileName: "resized-images",
        fileType: "zip",
        ...createDownloadTrackingCallbacks({
          feature: "internal_ad_asset_group",
          download_type: "bulk",
          ad_asset_group_id: adAssetGroupId,
          brand_id: Number.parseInt(brandId),
          resized: true
        }),
        onError: (error) => {
          console.error("Download failed:", error);
        }
      }
    );
    setCanResize(true);
    setCanDownloadResized(true);
  };

  const columns = [
    { field: "id", value: "Creative ID", width: 80 },
    {
      field: "src",
      value: "Image thumbnail",
      width: 200,
      renderCell: (row: Image) => {
        return <ImagePreviewCell image={row} />;
      }
    },
    {
      field: "sizes",
      value: "Resized Image thumbnail",
      width: 200,
      renderCell: (row: Image) => {
        if (row.sizes && row.sizes.length > 0) {
          return (
            <div>
              <Box>
                <ImagePreviewCell image={row.sizes[0]} />
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  disabled={isDownloading}
                  size="small"
                  onClick={() => {
                    handleResize(row);
                  }}
                  dataDogIdentifier="ad images table regenerate resize"
                >
                  Regenerate Resize
                </Button>
              </Box>
            </div>
          );
        }
        return <Typography color="textSecondary">No resized image</Typography>;
      }
    },
    {
      field: "tags",
      value: "Comments",
      width: 200,
      renderCell: (row: Image) => {
        return row.tags[0]?.comment;
      }
    },
    {
      field: "actions",
      value: "Actions",
      width: 50,
      renderCell: (row: Image) => {
        if (row.sizes && row.sizes.length > 0) {
          return (
            <Button
              variant="text"
              color="default"
              size="medium"
              onClick={(e) => handleMenuOpen(e, row.id)}
              dataDogIdentifier="ad images table ellipsis action"
            >
              …
            </Button>
          );
        }
      }
    }
  ];

  const menuActions: MenuAction[] = [
    {
      label: "View All Resized Images",
      onClick: handleViewAllResizedImages,
      visible: () => canDownloadResized
    }
  ];

  return (
    <>
      <Typography variant="h2" my={2}>
        Original Ad Images (Old)
      </Typography>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Box flex="1">
          <Button
            variant="contained"
            disabled={isDownloading}
            onClick={handleDownloadAll}
            dataDogIdentifier="ad images table download all"
          >
            Download All
          </Button>
          <Button
            variant="contained"
            sx={{ marginLeft: "16px" }}
            disabled={!canResize}
            onClick={handleResizeAll}
            dataDogIdentifier="ad images table resize all"
          >
            Resize All
          </Button>
          <Button
            variant="contained"
            sx={{ marginLeft: "16px" }}
            disabled={!canDownloadResized}
            onClick={handleDownloadResized}
            dataDogIdentifier="ad images table download all resized images"
          >
            Download All Resized Images
          </Button>
        </Box>
        <Box ml="auto">
          <Typography variant="body1">Total Ad Images: {images.length}</Typography>
        </Box>
      </Flex>
      {images.length > 0 ? (
        <Table columns={columns} rows={images} isLoading={isLoading} dataDogIdentifier="ad images" />
      ) : (
        <Typography color="textSecondary">No ad images found</Typography>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "110px"
          }
        }}
      >
        {menuActions
          .filter((action) => action.visible!(selectedRow))
          .map((action) => (
            <MenuItem key={action.label} onClick={action.onClick} size="medium">
              {action.label}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default AdImagesTable;
